<template>
  <div class="create-experiment">
    <h1>Create an Experiment</h1>
    <form @submit.prevent="submitForm">
      <!-- Experiment ID -->
      <div class="form-group">
        <label for="name">ID</label>
        <input
          type="text"
          id="name"
          v-model="experiment.name"
          required
          class="form-control"
        />
      </div>

      <!-- Location -->
      <div class="form-group">
        <label for="location">Location</label>
        <div class="dropdown-with-button">
          <select
            v-model="experiment.location_id"
            required
            class="dropdown-field form-control"
            :disabled="isLocationFixed || isLocationLockedToTarget"
          >
            <option disabled value="">Select a Location</option>
            <option
              v-for="location in locations"
              :key="location.id"
              :value="location.id"
            >
              {{ location.name }}
            </option>
          </select>
          <button
            type="button"
            @click="showCreateLocationDialog = true"
            class="create-button"
            title="Create new location"
          >
            + New
          </button>
        </div>
      </div>

      <!-- Project -->
      <div class="form-group">
        <label for="project_id">Project</label>
        <div class="dropdown-with-button">
          <select
            v-model="experiment.project_id"
            required
            class="dropdown-field form-control"
            :disabled="isProjectFixed"
            @change="onProjectChange"
          >
            <option disabled value="">Select a Project</option>
            <option v-for="project in projects" :key="project.id" :value="project.id">
              {{ project.name }}
            </option>
          </select>
          <button
            type="button"
            @click="showCreateProjectDialog = true"
            class="create-button"
            title="Create new project"
          >
            + New
          </button>
        </div>
      </div>

      <!-- Structure Target -->
      <div class="form-group" v-if="showStructureTargetDropdown">
        <label for="structure_element_id">Where in Project Structure</label>
        <div class="dropdown-with-button">
          <select
            id="structure_element_id"
            v-model="experiment.structure_element_id"
            class="dropdown-field form-control"
            @change="onStructureTargetChange"
            :disabled="isStructureTargetFixed"
            required
          >
            <option disabled value="">Select a Structure Element</option>
            <option
              v-for="target in structureTargets"
              :key="target.id"
              :value="target.id"
            >
              {{ formatStructureTargetLabel(target) }}
            </option>
          </select>
        </div>
      </div>

      <!-- Start Date -->
      <div class="form-group">
        <label for="start_date">Start Date</label>
        <input
          type="datetime-local"
          id="start_date"
          v-model="experiment.start_date"
          :class="{ 'is-invalid': validationErrors.includes('start_date') }"
          class="form-control"
        />
      </div>

      <!-- End Date -->
      <div class="form-group">
        <label for="end_date">End Date</label>
        <input
          type="datetime-local"
          id="end_date"
          v-model="experiment.end_date"
          :class="{ 'is-invalid': validationErrors.includes('end_date') }"
          class="form-control"
        />
      </div>

      <!-- Validation Errors -->
      <div v-if="validationErrors.length" class="form-group alert-container">
        <div class="alert alert-danger">
          <p v-for="error in validationErrors" :key="error">{{ error }}</p>
        </div>
      </div>

      <!-- Submit -->
      <button type="submit" class="submit-button">Create Experiment</button>
    </form>

    <!-- Create Location Dialog -->
    <div v-if="showCreateLocationDialog" class="modal-overlay" @click.self="showCreateLocationDialog = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Create New Location</h3>
          <button type="button" class="close-btn" @click="showCreateLocationDialog = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="location-name">Name *</label>
            <input
              type="text"
              id="location-name"
              v-model="newLocation.name"
              class="form-control"
              placeholder="e.g., Lab A, Building 2"
              required
            />
          </div>
          <div class="form-group">
            <label for="location-desc">Description</label>
            <textarea
              id="location-desc"
              v-model="newLocation.description"
              class="form-control"
              placeholder="Describe this location..."
              rows="3"
            ></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="cancel-btn" @click="showCreateLocationDialog = false">Cancel</button>
          <button type="button" class="save-btn" @click="createLocation">Create</button>
        </div>
      </div>
    </div>

    <!-- Create Project Dialog -->
    <div v-if="showCreateProjectDialog" class="modal-overlay" @click.self="showCreateProjectDialog = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Create New Project</h3>
          <button type="button" class="close-btn" @click="showCreateProjectDialog = false">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="project-name">Name *</label>
            <input
              type="text"
              id="project-name"
              v-model="newProject.name"
              class="form-control"
              placeholder="e.g., Project Alpha"
              required
            />
          </div>
          <div class="form-group">
            <label for="project-number">Project Number *</label>
            <input
              type="text"
              id="project-number"
              v-model="newProject.project_number"
              class="form-control"
              placeholder="e.g., PROJ-2024-001"
              required
            />
          </div>
          <div class="form-group">
            <label for="project-start">Start Date</label>
            <input
              type="date"
              id="project-start"
              v-model="newProject.start_date"
              class="form-control"
            />
          </div>
          <div class="form-group">
            <label for="project-end">End Date</label>
            <input
              type="date"
              id="project-end"
              v-model="newProject.end_date"
              class="form-control"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="cancel-btn" @click="showCreateProjectDialog = false">Cancel</button>
          <button type="button" class="save-btn" @click="createProject">Create</button>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="loading" class="overlay">
      <div class="spinner"></div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import axios from "axios";
import { API_ENDPOINTS } from "@/services/config";
import apiService from "@/services/apiService";
import { mapState } from "vuex";

const FORM_CACHE_KEY = "experiment_form";
const CACHE_EXPIRATION = 10 * 60 * 1000;

export default {
  data() {
    return {
      experiment: {
        name: "",
        location_id: "",
        location_name: "",

        user_id: "",
        project_id: "",

        structure_element_id: "",
        structure_element_name: "",

        start_date: "",
        end_date: "",
      },

      locations: [],
      projects: [],
      structureTargets: [],

      validationErrors: [],
      loading: false,

      isLocationFixed: false,
      isProjectFixed: false,
      isStructureTargetFixed: false,

      showStructureTargetDropdown: false,

      // Modal dialog states
      showCreateLocationDialog: false,
      showCreateProjectDialog: false,

      // Form data for new entities
      newLocation: { 
        name: "", 
        description: "" 
      },
      newProject: { 
        name: "", 
        project_number: "",
        start_date: "",
        end_date: "",
      },
    };
  },
  computed: {
    ...mapState(["loggedInUser"]),
    selectedProjectStructureSetId() {
      const selectedProjectId = Number(this.experiment.project_id);
      const selectedProject = this.projects.find((project) => Number(project.id) === selectedProjectId);
      return selectedProject?.structure_set_id ?? null;
    },
    selectedStructureTarget() {
      const selectedId = Number(this.experiment.structure_element_id);
      return this.structureTargets.find((item) => Number(item.id) === selectedId) || null;
    },
    isLocationLockedToTarget() {
      return !this.isLocationFixed && this.selectedStructureTarget?.location_id != null;
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },

  mounted() {
    this.loadCachedFormData();

    // Initialize default dates for new entity forms
    const today = new Date().toISOString().split('T')[0];
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    const endDate = oneYearFromNow.toISOString().split('T')[0];
    
    this.newProject.start_date = today;
    this.newProject.end_date = endDate;

    const {
      projectId,
      structureElementId,
      structureElementName,
      location_id,     // you used this key in query
      locationId,      // support either key, just in case
      location_name,   // you used this key in query
      locationName,    // support either key
    } = this.$route.query;

    // Location from route (support both location_id and locationId)
    const loc = location_id ?? locationId;
    if (loc) {
      this.experiment.location_id = Number(loc);
      this.isLocationFixed = true;
    }
    const locName = location_name ?? locationName;
    if (locName) {
      this.experiment.location_name = locName;
    }

    // Project from route
    if (projectId) {
      this.experiment.project_id = Number(projectId);
      this.isProjectFixed = true;
      this.showStructureTargetDropdown = true;
    }

    // Structure target from route
    if (structureElementId) {
      this.experiment.structure_element_id = Number(structureElementId);
      this.isStructureTargetFixed = true;
      this.showStructureTargetDropdown = true;
    }
    if (structureElementName) {
      this.experiment.structure_element_name = structureElementName;
    }

    this.fetchLocations();
    this.fetchProjects();

    // Ensure dropdown options are loaded so fixed selections render
    if (this.experiment.project_id) {
      this.onProjectChange();
    } else {
      this.showStructureTargetDropdown = !!this.experiment.project_id;
    }
  },

  methods: {
    loadCachedFormData() {
      const cachedRaw = localStorage.getItem(FORM_CACHE_KEY);
      if (!cachedRaw) return;

      const cachedData = JSON.parse(cachedRaw);
      if (!cachedData) return;

      const { data, timestamp } = cachedData;
      if (Date.now() - timestamp < CACHE_EXPIRATION) {
        this.experiment = data;
        this.showStructureTargetDropdown = !!this.experiment.project_id;
      } else {
        localStorage.removeItem(FORM_CACHE_KEY);
      }
    },

    cacheFormData() {
      localStorage.setItem(
        FORM_CACHE_KEY,
        JSON.stringify({ data: this.experiment, timestamp: Date.now() })
      );
    },

    validateForm() {
      this.validationErrors = [];
      const requiredFields = ["name", "location_id", "project_id", "structure_element_id"];

      requiredFields.forEach((f) => {
        if (!this.experiment[f]) this.validationErrors.push(`${f} is required`);
      });

      return this.validationErrors.length === 0;
    },

    async submitForm() {
      if (!this.validateForm()) {
        this.toast.error("Form is invalid!");
        return;
      }

      this.loading = true;
      try {
        const experimentDetails = {
          name: this.experiment.name,
          project_id: Number(this.experiment.project_id),
          project_structure_element_id: Number(this.experiment.structure_element_id),
          location_id: Number(this.experiment.location_id),
          user_id: this.loggedInUser.id,
          start_date: this.experiment.start_date || null,
          end_date: this.experiment.end_date || null,
        };

        const response = await apiService.createResource(
          API_ENDPOINTS.experiments,
          experimentDetails,
          this.loggedInUser.entra_id
        );

        this.toast.success("Experiment created successfully!");
        localStorage.removeItem(FORM_CACHE_KEY);

        this.$router.push({
          name: "ExperimentDetailPage",
          params: { id: response.experiment_id },
        });
      } catch (error) {
        this.toast.error("Failed to create experiment!");
        console.error("Failed to submit experiment details:", error);
      } finally {
        this.loading = false;
      }
    },

    async fetchLocations() {
      try {
        const response = await axios.get(API_ENDPOINTS.locations);
        this.locations = response.data;
      } catch (error) {
        console.error("Failed to fetch locations:", error);
      }
    },

    async fetchProjects() {
      try {
        const response = await axios.get(API_ENDPOINTS.projects, {
          params: { entra_id: this.loggedInUser.entra_id },
        });
        this.projects = response.data;
      } catch (error) {
        console.error("Failed to fetch projects:", error);
      }
    },

    async onProjectChange() {
      if (!this.isStructureTargetFixed) {
        this.experiment.structure_element_id = "";
        this.experiment.structure_element_name = "";
      }

      this.structureTargets = [];
      this.showStructureTargetDropdown = !!this.experiment.project_id;

      if (!this.experiment.project_id) return;

      try {
        const response = await apiService.getProjectStructureExperimentTargets(
          this.experiment.project_id,
          this.loggedInUser.entra_id,
          this.selectedProjectStructureSetId
        );
        this.structureTargets = Array.isArray(response)
          ? response
          : response?.items || response?.data?.items || [];

        if (this.experiment.structure_element_id) {
          this.onStructureTargetChange();
        }
      } catch (error) {
        console.error("Failed to fetch eligible experiment targets:", error);
        this.structureTargets = [];
      }
    },

    onStructureTargetChange() {
      const selectedId = Number(this.experiment.structure_element_id);
      const selectedTarget = this.structureTargets.find((item) => Number(item.id) === selectedId);
      this.experiment.structure_element_name = selectedTarget?.name || "";

      if (selectedTarget?.location_id && !this.isLocationFixed) {
        this.experiment.location_id = Number(selectedTarget.location_id);
        this.experiment.location_name = selectedTarget.location_name || "";
      }
    },

    formatStructureTargetLabel(target) {
      const locationLabel = target?.location_name || "No location";
      return `${target.name} — ${locationLabel}`;
    },

    async createLocation() {
      if (!this.newLocation.name.trim()) {
        this.toast.error("Location name is required");
        return;
      }

      try {
        const response = await apiService.createResource(
          API_ENDPOINTS.locations,
          this.newLocation,
          this.loggedInUser.entra_id
        );

        this.locations.push(response);
        this.experiment.location_id = response.id;
        this.showCreateLocationDialog = false;
        this.newLocation = { name: "", description: "" };
        this.toast.success("Location created successfully!");
      } catch (error) {
        console.error("Failed to create location:", error);
        this.toast.error("Failed to create location!");
      }
    },

    async createProject() {
      if (!this.newProject.name.trim()) {
        this.toast.error("Project name is required");
        return;
      }
      if (!this.newProject.project_number.trim()) {
        this.toast.error("Project number is required");
        return;
      }

      try {
        const payload = {
          name: this.newProject.name,
          project_number: this.newProject.project_number,
          start_date: this.newProject.start_date,
          end_date: this.newProject.end_date,
        };

        const response = await apiService.createResource(
          API_ENDPOINTS.projects,
          payload,
          this.loggedInUser.entra_id
        );

        this.projects.push(response);
        this.experiment.project_id = response.id;
        this.showCreateProjectDialog = false;
        this.newProject = { 
          name: "", 
          project_number: "",
          start_date: new Date().toISOString().split('T')[0],
          end_date: (() => {
            const date = new Date();
            date.setFullYear(date.getFullYear() + 1);
            return date.toISOString().split('T')[0];
          })(),
        };
        this.toast.success("Project created successfully!");
        
        // Reload workflows for the new project
        await this.onProjectChange();
      } catch (error) {
        console.error("Failed to create project:", error);
        this.toast.error("Failed to create project!");
      }
    },

  },

  watch: {
    experiment: {
      handler() {
        this.cacheFormData();
      },
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.create-experiment {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);

  h1 {
    margin-bottom: 0.75rem;
    font-size: 1.3rem;
    text-align: center;
  }

  form > .form-group {
    margin-bottom: 0.4rem;

    label {
      margin-bottom: 0;
      line-height: 1.2;
    }
  }

  .form-control {
    padding: 4px 10px;
    height: 32px;
    box-sizing: border-box;
  }

  select.form-control {
    height: 32px;
  }

  .submit-button {
    padding: 7px 10px;
    margin-top: 0.5rem;
  }
}

.dropdown-with-button {
  display: flex;
  gap: 8px;
  align-items: center;

  .dropdown-field {
    flex: 1;
  }

  .create-button {
    background-color: $primary-color;
    color: white;
    border: none;
    padding: 4px 10px;
    height: 32px;
    box-sizing: border-box;
    border-radius: 4px;
    font-size: 0.85rem;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: lighten($primary-color, 10%);
    }

    &:active {
      background-color: darken($primary-color, 10%);
    }
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 90%;
  overflow: hidden;

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px;
    border-bottom: 1px solid #eee;
    background-color: lighten($primary-color, 40%);

    h3 {
      margin: 0;
      color: $primary-color;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 28px;
      color: $primary-color;
      cursor: pointer;
      padding: 0;
      width: 30px;
      height: 30px;
      display: flex;
      align-items: center;
      justify-content: center;

      &:hover {
        color: darken($primary-color, 10%);
      }
    }
  }

  .modal-body {
    padding: 20px;

    .form-group {
      margin-bottom: 15px;

      label {
        display: block;
        margin-bottom: 5px;
        color: $primary-color;
        font-weight: 500;
      }

      input,
      textarea {
        width: 100%;
        padding: 10px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-family: inherit;

        &:focus {
          outline: none;
          border-color: $primary-color;
          box-shadow: 0 0 0 3px lighten($primary-color, 30%);
        }
      }

      textarea {
        resize: vertical;
      }
    }
  }

  .modal-footer {
    padding: 15px 20px;
    border-top: 1px solid #eee;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    background-color: #f9f9f9;

    button {
      padding: 8px 16px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 0.9rem;
      transition: all 0.2s ease;

      &.cancel-btn {
        background-color: #e0e0e0;
        color: #333;

        &:hover {
          background-color: #d0d0d0;
        }
      }

      &.save-btn {
        background-color: $primary-color;
        color: white;

        &:hover {
          background-color: lighten($primary-color, 10%);
        }

        &:active {
          background-color: darken($primary-color, 10%);
        }
      }
    }
  }
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;

  .spinner {
    border: 4px solid rgba(255, 255, 255, 0.3);
    border-top: 4px solid white;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
}
</style>